import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import {
  ArrowLeft,
  Camera,
  History,
  LayoutDashboard,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Share2,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import 'react-grid-layout/css/styles.css';

import { DataState } from '../../context/DataProvider';
import { useAccess } from '../../services/access';
import {
  getDashboard,
  getDashboardData,
  getSnapshot,
  restoreDashboard,
  updateDashboard,
} from '../../services/dashboardApi';
import DashboardWidget from '../../components/dashboards/DashboardWidget';
import useNoIndex from '../../hooks/useNoIndex';
import WidgetEditor from '../../components/dashboards/WidgetEditor';
import HistoryPanel from '../../components/dashboards/HistoryPanel';
import SharePanel from '../../components/dashboards/SharePanel';
import SnapshotsPanel from '../../components/dashboards/SnapshotsPanel';
import FilterBar from '../../components/dashboards/FilterBar';
import {
  buildLayouts,
  resizeTile,
  toSpecLayout,
} from '../../components/dashboards/layout';
import '../../components/dashboards/dashboardGrid.css';

const Grid = WidthProvider(Responsive);

const ROW_HEIGHT = 80;
const BREAKPOINTS = { lg: 768, sm: 0 };
const COLS = { lg: 12, sm: 1 };

/** Readable text for what fetchWithHandling throws (envelope error, 422 list, string). */
export const errorText = (e) => {
  if (Array.isArray(e))
    return e.map((d) => d?.msg || String(d)).join('; ') || 'Invalid dashboard.';
  return (typeof e === 'string' ? e : e?.message) || 'Something went wrong.';
};

const button =
  'flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50';
const plainButton = `${button} border-zinc-700 text-zinc-300 hover:bg-zinc-800`;
const primaryButton = `${button} border-[#5652CF]/30 bg-[#5652CF]/20 text-zinc-100 hover:bg-[#5652CF]/30`;

const DashboardPage = () => {
  const { dashboardId } = useParams();
  useNoIndex();
  const navigate = useNavigate();
  const { userGoogleId: accessToken } = DataState();
  const { can } = useAccess();
  const canManage = can('dashboard:manage');
  const canShare = can('dashboard:share');

  const [dashboard, setDashboard] = useState(null);
  const [widgetData, setWidgetData] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Edit mode: `draft` is the spec being edited; saved with the version it started from.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorFor, setEditorFor] = useState(undefined); // widget, null = new, undefined = closed
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [breakpoint, setBreakpoint] = useState('lg');
  // Filter bar choices ({ filterId: value }) and the values each can take.
  const [filterValues, setFilterValues] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [filtering, setFiltering] = useState(false);
  const filtersRef = useRef({});
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  // A saved snapshot being viewed (read-only), instead of live data.
  const [snapshot, setSnapshot] = useState(null);

  const loadData = useCallback(
    async (refresh = false, spec = null) => {
      try {
        const res = await getDashboardData(accessToken, dashboardId, {
          refresh,
          filters: filtersRef.current,
        });
        setWidgetData(res?.data?.widgets || {});
        setFilterOptions(res?.data?.filter_options || {});
      } catch (e) {
        if (!spec) throw e;
        // The page still renders; every data tile shows why it is empty.
        setWidgetData(
          Object.fromEntries(
            spec.widgets.map((w) => [w.id, { error: errorText(e) }]),
          ),
        );
      }
    },
    [accessToken, dashboardId],
  );

  useEffect(() => {
    if (!accessToken || !dashboardId) return undefined;
    let cancelled = false;
    setDashboard(null);
    setWidgetData({});
    setLoadError(null);
    setDraft(null);
    setSnapshot(null);
    filtersRef.current = {};
    setFilterValues({});
    (async () => {
      let loaded;
      try {
        loaded = (await getDashboard(accessToken, dashboardId))?.data;
      } catch (e) {
        if (!cancelled) setLoadError(errorText(e));
        return;
      }
      if (cancelled || !loaded) return;
      setDashboard(loaded);
      await loadData(false, loaded.spec);
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, dashboardId, loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData(true);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilters = async (values) => {
    filtersRef.current = values;
    setFilterValues(values);
    setFiltering(true);
    try {
      await loadData(false);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setFiltering(false);
    }
  };

  const openSnapshot = async (snapshotId) => {
    try {
      const res = await getSnapshot(accessToken, dashboardId, snapshotId);
      setSnapshot(res.data);
      setSnapshotsOpen(false);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  /** A newly saved dashboard: show it and fetch data for its (maybe new) sources. */
  const applySaved = async (saved) => {
    setDashboard(saved);
    setDraft(null);
    await loadData(false, saved.spec);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateDashboard(
        accessToken,
        dashboardId,
        draft,
        dashboard.version,
      );
      await applySaved(res.data);
      toast.success('Dashboard saved');
    } catch (e) {
      toast.error(
        e?.code === 'VERSION_CONFLICT'
          ? 'Someone changed this dashboard since you started editing. Cancel and reload to see it.'
          : errorText(e),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (restoreVersion) => {
    try {
      const res = await restoreDashboard(
        accessToken,
        dashboardId,
        restoreVersion,
        dashboard.version,
      );
      await applySaved(res.data);
      toast.success(`Restored version ${restoreVersion}`);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const handleWidgetSaved = (widget, size) => {
    setDraft((d) => {
      const exists = d.widgets.some((w) => w.id === widget.id);
      return {
        ...d,
        widgets: exists
          ? d.widgets.map((w) => (w.id === widget.id ? widget : w))
          : [...d.widgets, widget],
        layout: resizeTile(d.layout, widget.id, size),
      };
    });
    setEditorFor(undefined);
  };

  const handleRemove = (id) =>
    setDraft((d) => ({
      ...d,
      widgets: d.widgets.filter((w) => w.id !== id),
      layout: (d.layout || []).filter((item) => item.id !== id),
    }));

  const handleLayoutChange = (_current, all) => {
    // Only the wide layout is stored; the phone column is derived from it.
    if (!draft || breakpoint !== 'lg' || !all?.lg) return;
    setDraft((d) => ({ ...d, layout: toSpecLayout(all.lg) }));
  };

  const editing = draft !== null;
  // Shared viewers read only; history, edit and sharing are the owner's.
  const isOwner = dashboard?.can_edit !== false;

  /** From an error tile: open this widget's editor, entering edit mode if needed. */
  const editWidget = (widget) => {
    if (!editing) setDraft(JSON.parse(JSON.stringify(dashboard.spec)));
    setEditorFor(widget);
  };
  const spec = draft || snapshot?.spec || dashboard?.spec;
  const shownData = snapshot ? snapshot.widgets : widgetData;
  const layouts = useMemo(() => (spec ? buildLayouts(spec) : null), [spec]);
  const savedIds = useMemo(
    () => new Set((dashboard?.spec?.widgets || []).map((w) => w.id)),
    [dashboard],
  );
  const fetchedAt = useMemo(() => {
    const times = Object.values(shownData)
      .map((d) => d?.fetched_at)
      .filter(Boolean)
      .sort();
    return times.length ? new Date(times[0]) : null;
  }, [shownData]);

  if (loadError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-[#0a0a0c] px-4 text-center text-zinc-300">
        <LayoutDashboard size={28} className="text-zinc-600" />
        <p className="text-sm">{loadError}</p>
        <button onClick={() => navigate('/chat')} className={plainButton}>
          Go to chat
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a0c]">
        <Loader2 className="animate-spin text-zinc-500" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#0a0a0c] text-zinc-200">
      <header className="flex flex-wrap items-center gap-2 border-b border-zinc-800 px-4 py-3">
        {!editing && can('chat:send') && (
          <button
            onClick={() =>
              navigate(
                dashboard.chat_id ? `/chat/${dashboard.chat_id}` : '/chat',
              )
            }
            className={plainButton}
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back to chat
          </button>
        )}
        <div className="min-w-0 flex-1 px-1">
          {editing ? (
            <input
              aria-label="Dashboard title"
              value={draft.title}
              maxLength={120}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full max-w-md rounded-lg border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm font-semibold text-zinc-100 focus:border-[#5652CF] focus:outline-none"
            />
          ) : (
            <>
              <h1 className="truncate text-sm font-semibold text-zinc-100">
                {dashboard.title}
              </h1>
              {spec.description && (
                <p className="truncate text-xs text-zinc-500">
                  {spec.description}
                </p>
              )}
            </>
          )}
        </div>

        {snapshot ? (
          <>
            <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-200">
              Snapshot · {snapshot.name} ·{' '}
              {new Date(snapshot.created_at).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            <button
              onClick={() => setSnapshotsOpen(true)}
              className={plainButton}
            >
              <Camera size={14} aria-hidden="true" />
              Snapshots
            </button>
            <button onClick={() => setSnapshot(null)} className={primaryButton}>
              Back to live
            </button>
          </>
        ) : editing ? (
          <>
            <button
              onClick={() => setEditorFor(null)}
              disabled={draft.widgets.length >= 12}
              title={
                draft.widgets.length >= 12 ? 'At most 12 widgets' : undefined
              }
              className={plainButton}
            >
              <Plus size={14} aria-hidden="true" />
              Add widget
            </button>
            <button
              onClick={() => setDraft(null)}
              disabled={saving}
              className={plainButton}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !draft.title.trim() || !draft.widgets.length}
              className={primaryButton}
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save
            </button>
          </>
        ) : (
          <>
            {fetchedAt && (
              <span className="hidden text-[11px] text-zinc-500 sm:inline">
                Updated{' '}
                {fetchedAt.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {!isOwner && (
              <span className="rounded-md border border-zinc-700 px-2 py-0.5 text-[11px] text-zinc-400">
                Shared with you · read-only
              </span>
            )}
            <button
              onClick={() => setSnapshotsOpen(true)}
              className={plainButton}
            >
              <Camera size={14} aria-hidden="true" />
              Snapshots
            </button>
            {isOwner && (
              <button
                onClick={() => setHistoryOpen(true)}
                className={plainButton}
              >
                <History size={14} aria-hidden="true" />
                History
              </button>
            )}
            {isOwner && canShare && (
              <button
                onClick={() => setShareOpen(true)}
                className={plainButton}
              >
                <Share2 size={14} aria-hidden="true" />
                Share
              </button>
            )}
            {isOwner && canManage && (
              <button
                onClick={() =>
                  setDraft(JSON.parse(JSON.stringify(dashboard.spec)))
                }
                className={plainButton}
              >
                <Pencil size={14} aria-hidden="true" />
                Edit
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={primaryButton}
            >
              <RefreshCw
                size={14}
                className={refreshing ? 'animate-spin' : ''}
                aria-hidden="true"
              />
              Refresh
            </button>
          </>
        )}
      </header>

      {editing && (
        <p className="border-b border-zinc-800 bg-zinc-900/60 px-4 py-1.5 text-[11px] text-zinc-400">
          {breakpoint === 'lg'
            ? 'Drag tiles to move them, drag a corner to resize. New or changed data loads after Save.'
            : 'Moving tiles needs a wider screen. You can still edit, add and remove widgets.'}
        </p>
      )}

      {!editing && !snapshot && (
        <FilterBar
          filters={spec.filters}
          options={filterOptions}
          values={filterValues}
          onChange={handleFilters}
          disabled={filtering}
        />
      )}
      {snapshot && Object.keys(snapshot.filters || {}).length > 0 && (
        <p className="border-b border-zinc-800 px-4 py-1.5 text-[11px] text-zinc-500">
          Saved with filters:{' '}
          {Object.entries(snapshot.filters)
            .map(([id, v]) => {
              const f = (spec.filters || []).find((x) => x.id === id);
              return `${f?.label || id}: ${v}`;
            })
            .join(' · ')}
        </p>
      )}

      <main className="min-h-0 flex-1 overflow-y-auto px-2 py-2 md:px-4">
        <Grid
          layouts={layouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          isDraggable={editing && breakpoint === 'lg'}
          isResizable={editing && breakpoint === 'lg'}
          draggableCancel=".no-drag"
          className={editing ? 'dashboard-grid--editing' : ''}
          onBreakpointChange={setBreakpoint}
          onLayoutChange={handleLayoutChange}
        >
          {spec.widgets.map((w) => (
            <div key={w.id} className={editing ? 'cursor-move' : ''}>
              <DashboardWidget
                widget={w}
                data={shownData[w.id]}
                pending={editing && !savedIds.has(w.id)}
                onRetry={editing || snapshot ? undefined : handleRefresh}
                onEdit={
                  isOwner && canManage && !snapshot
                    ? () => editWidget(w)
                    : undefined
                }
              />
              {editing && (
                <div className="no-drag absolute right-2 top-2 flex gap-1">
                  <button
                    onClick={() => setEditorFor(w)}
                    title="Edit widget"
                    aria-label={`Edit ${w.title}`}
                    className="rounded-md border border-zinc-700 bg-zinc-900 p-1 text-zinc-300 hover:bg-zinc-800"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => handleRemove(w.id)}
                    disabled={spec.widgets.length <= 1}
                    title="Remove widget"
                    aria-label={`Remove ${w.title}`}
                    className="rounded-md border border-zinc-700 bg-zinc-900 p-1 text-zinc-300 hover:bg-red-900/40 disabled:opacity-40"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </Grid>
      </main>

      {editorFor !== undefined && (
        <WidgetEditor
          widget={editorFor}
          size={(() => {
            const item = (draft.layout || []).find(
              (i) => i.id === editorFor?.id,
            );
            return item ? { w: item.w, h: item.h } : undefined;
          })()}
          takenIds={
            new Set(
              draft.widgets
                .map((w) => w.id)
                .filter((id) => id !== editorFor?.id),
            )
          }
          onSave={handleWidgetSaved}
          onClose={() => setEditorFor(undefined)}
        />
      )}
      {shareOpen && (
        <SharePanel
          accessToken={accessToken}
          dashboard={dashboard}
          onClose={() => setShareOpen(false)}
        />
      )}
      {snapshotsOpen && (
        <SnapshotsPanel
          accessToken={accessToken}
          dashboardId={dashboardId}
          filters={filterValues}
          canSave={canManage}
          onOpen={openSnapshot}
          onClose={() => setSnapshotsOpen(false)}
        />
      )}
      {historyOpen && (
        <HistoryPanel
          accessToken={accessToken}
          dashboardId={dashboardId}
          currentVersion={dashboard.version}
          canRestore={canManage}
          onRestore={handleRestore}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
